import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile widget for public phone-code requests, rendered
 * `interaction-only`. It shares the consumer's sitekey secret (deploy-pages.yml),
 * which is observed rendering as Managed on pay.parkify.com, so a flagged visitor
 * here should see a box too.
 *
 * The Worker enforces the phone-send challenge when the production flag is on.
 * Keep the component dark when no public site key is baked into a build so
 * local/demo builds remain usable and the server-side flag stays authoritative.
 */
const SITEKEY = import.meta.env.VITE_DEMO === '1' ? undefined : import.meta.env.VITE_TURNSTILE_SITEKEY
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove: (id: string) => void
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let scriptPromise: Promise<void> | null = null
function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('turnstile script failed to load'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export function isTurnstileConfigured(): boolean { return !!SITEKEY }

interface Props {
  /** Receives the token on solve, and '' on expiry/error (Cloudflare retries
   *  and refreshes those itself, so the widget is solving again). */
  onToken: (token: string) => void
  /** Bump to force a fresh widget + token. */
  refreshKey?: number
  /** True while Cloudflare is showing an interactive challenge, false once it
   *  is gone. The only honest signal that there is something to tap. */
  onInteractive?: (on: boolean) => void
  /** Called as each widget is rendered (mount or refreshKey bump), so a token
   *  gate knows a solve is in flight and does not restart it. */
  onRender?: () => void
  /** This widget will not produce a token: the script was blocked or
   *  unreachable, it loaded without its API, render threw, or the browser is
   *  unsupported. Waiting will not help; the caller should offer a fresh one. */
  onError?: () => void
}

export function Turnstile({ onToken, refreshKey = 0, onInteractive, onRender, onError }: Props) {
  const container = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    if (!SITEKEY) return
    let cancelled = false
    const removeWidget = () => {
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current) } catch { /* already gone */ }
      }
      widgetId.current = null
    }
    // A widget that cannot produce a token goes to the caller's error path. With
    // TURNSTILE_PHONE_ENABLED on, the API bounces a tokenless /phone/start
    // (`turnstile_required`), so staying quiet here is a dead end for the resident.
    // Same treatment as the consumer widget (#4093).
    const fail = () => {
      if (!cancelled) onError?.()
    }

    void loadScript().then(() => {
      if (cancelled || !container.current) return
      if (!window.turnstile) {
        // A captive portal or CSP-stubbed script can fire onload without the API.
        // Forget the load so the next widget requests the script again.
        scriptPromise = null
        throw new Error('turnstile script loaded without its API')
      }
      removeWidget()
      // Before render, not after: Cloudflare may call unsupported-callback from
      // inside render, and that failure must not be overwritten by "solving".
      onRender?.()
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: SITEKEY,
        appearance: 'interaction-only',
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
        'before-interactive-callback': () => { if (!cancelled) onInteractive?.(true) },
        'after-interactive-callback': () => { if (!cancelled) onInteractive?.(false) },
        'unsupported-callback': fail,
        // No timeout-callback, deliberately: on a Managed widget refresh-timeout
        // defaults to auto, so Cloudflare redraws the challenge itself.
      })
    }).catch(() => {
      // Script blocked or unreachable, loaded without its API, or render threw:
      // no widget, so no token. A failed load clears scriptPromise, so the next
      // widget requests the script again.
      fail()
    })

    return () => { cancelled = true; removeWidget() }
  }, [onToken, onInteractive, onRender, onError, refreshKey])

  if (!SITEKEY) return null
  return <div ref={container} className="pk-turnstile" aria-hidden="true" />
}
